/**
 * The room summary's refresh rules (`GET /api/rooms/{roomId}/summary`, performance rules 12 and 13):
 * what the shell and Overview draw, which changes on writes and with time.
 *
 * Pure, and importing only the pure `roleInForce` at runtime, so `node --test` can drive it through a real
 * TanStack observer on a fake clock (`summary.test.js`).
 */

import { roleInForce } from './roleInForce.ts';

/**
 * How long a summary is fresh: five minutes, the staleness the gate accepted for another operator's
 * change. This app's own writes never wait for it — they invalidate the summary at once.
 */
export const SUMMARY_FRESH_MS = 5 * 60_000;

/** Asked a second after the boundary, so the server's clock is past it too. */
export const BOUNDARY_MARGIN_MS = 1_000;

interface Boundary {
  nextDayStartsAt: string;
}

interface Observed {
  state: { data?: Boundary | undefined };
}

/**
 * The room whose summary the shell reads: none until a role is in force (known gap 70, #159) — `roleInForce`, the
 * rule the rooms list follows too (#544), so the two reads cannot disagree about when the app may ask.
 */
export function summaryRoomFor(roomId: string | null, offerable: number | null, actingRole: string | null): string | null {
  return roleInForce(offerable, actingRole) ? roomId : null;
}

/** Milliseconds until the room's next gaming day, from what the server last said; null with nothing yet. */
export function untilNextDay(data: Boundary | undefined, now: number = Date.now()): number | null {
  if (!data) return null;
  const at = Date.parse(data.nextDayStartsAt);
  return Number.isNaN(at) ? null : at - now;
}

/**
 * Why Overview's fantasy section is empty when no contest is in flight (Open or Locked, gate answer Q5).
 * It names the drafts — those point at Publish, as the leaderboards section's unstarted boards point at
 * Start — and the closed contests, from the counts the summary classified. Never "completed": the server
 * has no such status, and testing for it is how closed contests were listed as current (known gap 32).
 */
export function contestsEmptyNotice(contests: { draft: number; closed: number }, total: number): string {
  if (total === 0) return 'No fantasy contests yet.';
  const drafts =
    contests.draft === 0
      ? ''
      : contests.draft === 1
        ? ' One is a draft that has not been published.'
        : ` ${contests.draft} are drafts that have not been published.`;
  const closed =
    contests.closed === 0 ? '' : contests.closed === 1 ? ' One has closed.' : ` ${contests.closed} have closed.`;
  return `No contests are running right now.${drafts}${closed}`;
}

/**
 * Whether the Leaderboards screen marks a board "In window": exactly when the room summary lists it among
 * the running boards — the one decision the server made at the room's gaming day, which Overview lists
 * too. So the two screens cannot disagree (gate answer Q6); comparing the board's dates with the browser's
 * UTC date here called a board ending today over from 17:00 in Los Angeles (known gap 22). Nothing is
 * claimed before the summary answers.
 */
export function inWindow(
  board: { id: string },
  summary: { leaderboards: { running: { id: string }[] } } | undefined,
): boolean {
  return summary?.leaderboards.running.some((b) => b.id === board.id) ?? false;
}

/**
 * The query options the summary hook spreads in.
 *
 * - **staleTime**: five minutes, or less when the room's day ends sooner — an answer about yesterday is
 *   never fresh.
 * - **refetchInterval**: the time to the room's next day. Stale is not enough on its own: this app does
 *   not refetch on focus, and a stale query is only refetched when something new observes it, so an
 *   Overview left open overnight would keep yesterday's rundown under a "Today" label. Room-web used to
 *   recompute the date on every render; this is what keeps that true once the server decides the date.
 * - **refetchIntervalInBackground**: one small request a day; a hidden tab must not wake up on yesterday.
 */
export const summaryRefresh = {
  staleTime: (query: Observed): number => {
    const left = untilNextDay(query.state.data);
    return left === null ? SUMMARY_FRESH_MS : Math.max(0, Math.min(SUMMARY_FRESH_MS, left));
  },
  refetchInterval: (query: Observed): number | false => {
    const left = untilNextDay(query.state.data);
    return left === null ? false : Math.max(left, 0) + BOUNDARY_MARGIN_MS;
  },
  refetchIntervalInBackground: true,
};
